/* ============================================================
   AM2T — global.js
   Loaded on every page (deferred, from layout/theme.liquid).
   - AM2T core: routes, translated strings, money format, a single
     shared /cart.js read per page, pack helpers.
   - <drawer-element> (mobile menu + cart drawer)
   - Header cart counter
   - Reveal-on-scroll observer
   Vanilla ES, no dependencies.
   ============================================================ */

(() => {
  'use strict';

  document.documentElement.classList.add('js-loaded');

  /* === AM2T core ==================================================
     window.AM2T.routes and window.AM2T.strings are printed by
     layout/theme.liquid (Liquid `routes` + locale strings), so every
     script works under a language/market prefix such as /en/.
     ================================================================= */
  const AM2T = (window.AM2T = window.AM2T || {});
  const routes = (AM2T.routes = AM2T.routes || {});
  routes.root = routes.root || '/';
  routes.cart = routes.cart || '/cart';
  routes.cart_add = routes.cart_add || '/cart/add';
  routes.cart_change = routes.cart_change || '/cart/change';
  routes.cart_update = routes.cart_update || '/cart/update';
  routes.products = routes.products || routes.root + 'products';

  /** Translated string (js.* keys) with [var] interpolation. Falls back to the key. */
  AM2T.t = (key, vars) => {
    let str = (AM2T.strings && AM2T.strings[key]) || key;
    if (vars) {
      // Placeholders are written as [name] in locales/*.json (js.* keys)
      // so Liquid's own {{ }} interpolation never touches them.
      Object.keys(vars).forEach((k) => {
        str = str.split('[' + k + ']').join(vars[k]);
      });
    }
    return str;
  };

  AM2T.escapeHtml = (str) =>
    String(str ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  /** Money from cents, in the shop currency and page language. */
  AM2T.formatMoney = (cents, currency, opts = {}) => {
    if (typeof cents !== 'number') return '';
    const cur = currency || AM2T.currency || 'EUR';
    const digits = opts.noDecimals ? 0 : 2;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: cur,
        minimumFractionDigits: digits,
        maximumFractionDigits: digits
      }).format(cents / 100);
    } catch {
      return `${(cents / 100).toFixed(digits)} ${cur}`;
    }
  };

  /** JSON fetch helper that throws on HTTP errors. */
  AM2T.fetchJSON = async (url, options = {}) => {
    const res = await fetch(url, {
      ...options,
      headers: { Accept: 'application/json', ...(options.headers || {}) }
    });
    if (!res.ok) {
      const err = new Error(`HTTP ${res.status}`);
      err.status = res.status;
      try { err.body = await res.json(); } catch { /* not JSON */ }
      throw err;
    }
    return res.json();
  };

  /* --- Shared cart state -----------------------------------------
     Before: cart drawer, cart progress, pack upsell and pack engine
     each fetched /cart.js on load (4 requests per page). Now they all
     share one promise. Every 'cart:update' event that carries a cart
     refreshes the cache, so readers always get the latest state. */
  let cartPromise = null;
  AM2T.cart = {
    get(force = false) {
      if (force || !cartPromise) {
        cartPromise = AM2T.fetchJSON(routes.cart + '.js').catch((err) => {
          cartPromise = null;
          throw err;
        });
      }
      return cartPromise;
    },
    set(cart) {
      if (cart) cartPromise = Promise.resolve(cart);
    },
    /** POST /cart/add.js with items[], then return the fresh cart. */
    async add(items) {
      await AM2T.fetchJSON(routes.cart_add + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });
      return this.get(true);
    },
    /** POST /cart/change.js and return the cart it answers with. */
    async change(key, quantity) {
      const cart = await AM2T.fetchJSON(routes.cart_change + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });
      this.set(cart);
      return cart;
    }
  };

  /** Dispatch the theme-wide cart event. */
  AM2T.publishCart = (cart, source, extra = {}) => {
    document.dispatchEvent(
      new CustomEvent('cart:update', { bubbles: true, detail: { cart, source, ...extra } })
    );
  };

  document.addEventListener('cart:update', (e) => {
    if (e.detail && e.detail.cart) AM2T.cart.set(e.detail.cart);
  });

  /* --- Competition Pack helpers (ids printed by snippets/pack-config) */
  AM2T.packIds = () => AM2T.packEligibleIds || null;
  AM2T.packState = (cart) => {
    const ids = AM2T.packIds();
    const items = (cart && cart.items) || [];
    const has = (list) => Array.isArray(list) && items.some((i) => list.includes(i.variant_id));
    const hasTop = !!ids && has(ids.tops);
    const hasBottom = !!ids && has(ids.bottoms);
    const hasSocks = !!ids && has(ids.socks);
    return {
      hasTop,
      hasBottom,
      hasSocks,
      complete: hasTop && hasBottom && hasSocks,
      any: hasTop || hasBottom || hasSocks
    };
  };

  /** True for gift lines (the pack / threshold bottles). */
  AM2T.isGiftLine = (item) => {
    const ids = AM2T.packIds() || {};
    if (item.properties && (item.properties._gift === 'true' || item.properties._gift === true)) return true;
    return !!item.variant_id && (item.variant_id === ids.orangeBottleVariantId || item.variant_id === ids.greyBottleVariantId);
  };

  /** Subtotal of what the shopper actually pays for (gift lines excluded),
      used for the threshold gift so a gift never unlocks another gift. */
  AM2T.paidSubtotal = (cart) =>
    ((cart && cart.items) || []).reduce(
      (sum, item) => (AM2T.isGiftLine(item) ? sum : sum + (item.final_line_price || 0)),
      0
    );

  /* --- Theme editor: re-run section initialisers after a section
     is re-rendered. Section scripts register with AM2T.onSection. */
  const sectionInits = [];
  AM2T.onSection = (init) => {
    sectionInits.push(init);
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
    } else {
      init(document);
    }
  };
  document.addEventListener('shopify:section:load', (e) => {
    sectionInits.forEach((init) => init(e.target));
  });

  /* === <drawer-element> ============================================
     Generic off-canvas drawer. Triggers are any element with
     aria-controls="<drawer-id>". Closed by [data-drawer-close],
     overlay click, Escape, or matching auto-close-media.
     Used by the mobile menu, and (Build 2) the cart drawer.
     ================================================================= */
  class DrawerElement extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('[data-drawer-panel]');
      this.overlay = this.querySelector('[data-drawer-overlay]');

      this.triggers = this.id
        ? Array.from(document.querySelectorAll(`[aria-controls="${this.id}"]`))
        : [];
      this.triggers.forEach((t) => t.addEventListener('click', this.toggle));

      this.querySelectorAll('[data-drawer-close]').forEach((el) =>
        el.addEventListener('click', this.close)
      );
      this.overlay?.addEventListener('click', this.close);

      document.addEventListener('keydown', this.onKeydown);

      const mq = this.getAttribute('auto-close-media');
      if (mq && window.matchMedia) {
        this._mql = window.matchMedia(mq);
        this._mqlHandler = (e) => {
          if (e.matches && this.isOpen) this.close();
        };
        this._mql.addEventListener?.('change', this._mqlHandler);
      }
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKeydown);
      this._mql?.removeEventListener?.('change', this._mqlHandler);
    }

    get isOpen() {
      return this.hasAttribute('open');
    }

    toggle = (e) => {
      e?.preventDefault();
      this.isOpen ? this.close() : this.open();
    };

    open = () => {
      if (this.isOpen) return;
      this.lastFocus = document.activeElement;
      this.setAttribute('open', '');
      this.setAttribute('aria-hidden', 'false');
      this.triggers.forEach((t) => t.setAttribute('aria-expanded', 'true'));
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';

      requestAnimationFrame(() => {
        const focusable = this.panel?.querySelector(
          '[data-drawer-close], a[href], button:not([disabled])'
        );
        focusable?.focus();
      });

      this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
    };

    close = () => {
      if (!this.isOpen) return;
      this.removeAttribute('open');
      this.setAttribute('aria-hidden', 'true');
      this.triggers.forEach((t) => t.setAttribute('aria-expanded', 'false'));
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';

      if (this.lastFocus && typeof this.lastFocus.focus === 'function') {
        this.lastFocus.focus();
      }

      this.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
    };

    onKeydown = (e) => {
      if (!this.isOpen) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        this.close();
        return;
      }
      if (e.key === 'Tab') this.trapFocus(e);
    };

    trapFocus(e) {
      const focusable = this.panel?.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable?.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  customElements.define('drawer-element', DrawerElement);

  /* === Cart counter sync ============================================
     Listens for 'cart:update' (dispatched after add-to-cart fetches) and
     updates the header cart counter with a brief scale microanimation.
     The cart drawer (Build 2D) will hook into the same event.
     ================================================================= */
  document.addEventListener('cart:update', (e) => {
    const cart = e.detail?.cart;
    if (!cart) return;

    const cartLink = document.querySelector('[data-cart-link]');
    if (!cartLink) return;

    let counter = cartLink.querySelector('.site-header__cart-count');

    if (cart.item_count > 0) {
      if (!counter) {
        counter = document.createElement('span');
        counter.className = 'site-header__cart-count';
        counter.setAttribute('aria-hidden', 'true');
        cartLink.appendChild(counter);
      }
      counter.textContent = cart.item_count;

      const baseLabel = cartLink.dataset.cartLabelBase;
      if (baseLabel) {
        cartLink.setAttribute('aria-label', `${baseLabel} (${cart.item_count})`);
      }

      counter.animate?.(
        [
          { transform: 'scale(1)' },
          { transform: 'scale(1.1)' },
          { transform: 'scale(1)' }
        ],
        { duration: 200, easing: 'ease-out' }
      );
    } else {
      if (counter) counter.remove();
      const baseLabel = cartLink.dataset.cartLabelBase;
      if (baseLabel) cartLink.setAttribute('aria-label', `${baseLabel} (0)`);
    }
  });

  /* === Reveal-on-scroll ===========================================
     Adds .is-visible to .reveal elements as they enter the viewport.
     (Moved here from an inline script in layout/theme.liquid.)
     ================================================================= */
  const revealAll = (root) => {
    const els = root.querySelectorAll('.reveal:not(.is-visible)');
    if (!('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -10% 0px' }
    );
    els.forEach((el) => observer.observe(el));
  };
  AM2T.onSection(revealAll);
})();
